// components/views/DesignStudioSkin.tsx
import React from "react";
import { AgentProfile, AIConfig } from "../../types";
import "../../App.css";
import "../../ButtonNew.css";

type Props = {
  // Data
  agents: AgentProfile[];
  selectedAgent: string;
  currentAgent?: AgentProfile;
  aiConfig: AIConfig | null;
  isGeneratingTests: boolean;
  isTestRunning: boolean;
  isTasksReady: boolean;
  logsCount: number;
  resultsCount: number;

  // Actions
  onSelectAgent: (name: string) => void;
  onUploadAgent: () => void;
  onOpenTemplates: () => void;

  onOpenTestCases: () => void;   // AI Scenarios
  onGenerateTests: () => void;   // Generate Scenarios
  onOpenTestData: () => void;    // AI Data
  onOpenDb: () => void;          // View DB

  onExecute: () => void;         // Execute Tests
  onOpenReport: () => void;      // Test Report
  onOpenDefects: () => void;
  onOpenResults: () => void;     // Open Results (modal)

  onOpenLogs: () => void;        // focus fixed Logs panel

  onOpenApiModal: () => void;    // Create API
  onOpenMockModal: () => void;   // Create MOCK

  onOpenAgentInfo: () => void;
  onOpenAIConfig: () => void;
  onOpenGenerator: () => void;

  onOpenDashboard: () => void;
  onOpenEnvironment: () => void;

};

export default function DesignStudioSkin({
  agents,
  selectedAgent,
  currentAgent,
  aiConfig,
  isGeneratingTests,
  isTestRunning,
  isTasksReady,
  logsCount,
  resultsCount,

  onSelectAgent,
  onUploadAgent,
  onOpenTemplates,

  onOpenTestCases,
  onGenerateTests,
  onOpenTestData,
  onOpenDb,

  onExecute,
  onOpenReport,
  onOpenDefects,
  onOpenResults,

  onOpenLogs,

  onOpenApiModal,
  onOpenMockModal,

  // ✨ ADD THESE TWO:
  onOpenAgentInfo,
  onOpenAIConfig,
  onOpenGenerator,

  onOpenDashboard,
  onOpenEnvironment,

}: Props) {
  const aiConfigured = !!aiConfig?.testwerk_api_key_configured;

  return (
    <div className="view-container">
      <div className="ribbon-stack">
        {/* Agent Management */}
        <section className="ribbon">
          <div className="ribbon-header">
            <div>
              <div className="ribbon-title">Agent Management</div>
              {/*
              <div className="ribbon-sub">Pick the Agent Under Test and manage its assets.</div>
              */}
            </div>
          </div>

          <div className="ribbon-content">
            {/* Agent selector */}
            <div className="group" style={{ flexWrap: "wrap" }}>


              {agents.filter(a => a.isActive).map(agent => (
                <label key={agent.id} className={rowClass(selectedAgent === agent.name)}>
                  <input
                    type="radio"
                    name="agent-selection"
                    value={agent.name}
                    checked={selectedAgent === agent.name}
                    onChange={(e) => onSelectAgent(e.target.value)}
                  />
                  <span className="ribbon-agent-name" style={{ flex: 1 }}>{agent.name}</span>
                  <span className="ribbon-pill">{agent.testCaseCount} tests</span>
                </label>
              ))}
            </div>

            {/* Actions */}
            <div className="ribbon-actions">
              <button className="pushable push-primary btn-sm" onClick={onUploadAgent}>
                <span className="shadow"></span><span className="edge"></span>
                <span className="front">Upload Agent</span>
              </button>
              <button className="pushable push-primary btn-sm" onClick={onOpenTemplates}>
                <span className="shadow"></span><span className="edge"></span>
                <span className="front">Agent Templates</span>
              </button>
            </div>
          </div>

          {/* Selected agent summary */}
          <div className="ribbon-actions">

            <button
              className="pushable push-secondary btn-sm"
              onClick={onOpenAgentInfo}
              disabled={!selectedAgent}
              title="View selected agent details"
            >
              <span className="shadow"></span><span className="edge"></span><span className="front">View</span>
            </button>
          </div>
        </section>

        {/* Design */}
        <section className="ribbon">
          <div className="ribbon-header">
            <div>
              <div className="ribbon-title">Design</div>
              <div className="ribbon-sub">
                {selectedAgent ? `Agent: ${selectedAgent}` : 'None'}
              </div>
            </div>
          </div>

          <div className="ribbon-content">
            <div className="group">
              <button className="pushable push-primary btn-sm" onClick={onOpenTestCases}>
                <span className="shadow"></span><span className="edge"></span>
                <span className="front">AI Scenarios</span>
              </button>

              <button className="pushable push-primary btn-sm" onClick={onOpenGenerator}>
                <span className="shadow"></span><span className="edge"></span>
                <span className="front">GeneratorRx</span>
              </button>

              <button
                className="pushable push-primary btn-sm"
                onClick={onGenerateTests}
                disabled={isGeneratingTests || !aiConfigured}
              >
                <span className="shadow"></span><span className="edge"></span>
                <span className="front">{isGeneratingTests ? "Generating..." : "Generate Scenarios"}</span>
              </button>

              <button className="pushable push-primary btn-sm" onClick={onOpenTestData}>
                <span className="shadow"></span><span className="edge"></span>
                <span className="front">AI Data</span>
              </button>

              <button className="pushable push-primary btn-sm" onClick={onOpenDb}>
                <span className="shadow"></span><span className="edge"></span>
                <span className="front">View DB</span>
              </button>
            </div>
          </div>
        </section>

        {/* Execute disabled={isGeneratingTests || isTestRunning || !isTasksReady} */}
        <section className="ribbon">
          <div className="ribbon-header">
            <div>
              <div className="ribbon-title">Execution</div>
              {/* 
              <div className="ribbon-sub">Run tests, view reports & results.</div>
              */}
            </div>
          </div>

          <div className="ribbon-content">
            <div className="group">
              <button
                className="pushable push-primary btn-sm"
                onClick={onExecute}
                disabled={isGeneratingTests || isTestRunning || !isTasksReady}
              >
                <span className="shadow"></span><span className="edge"></span>
                <span className="front">Execute Tests</span>
              </button>

              <button className="pushable push-primary btn-sm" onClick={onOpenResults}>
                <span className="shadow"></span><span className="edge"></span>
                <span className="front">Test Results ({resultsCount})</span>
              </button>

              <button className="pushable push-primary btn-sm" onClick={onOpenReport}>
                <span className="shadow"></span><span className="edge"></span>
                <span className="front">Test Report</span>
              </button>

              <button className="pushable push-primary btn-sm" onClick={onOpenDashboard}>
                <span className="shadow"></span><span className="edge"></span>
                <span className="front">Dashboard</span>
              </button>

              <button className="pushable push-primary btn-sm" onClick={onOpenDefects}>
                <span className="shadow"></span><span className="edge"></span>
                <span className="front">Defects</span>
              </button>


            </div>
          </div>
        </section>

        {/* Integrations */}
        <section className="ribbon">
          <div className="ribbon-header">
            <div>
              <div className="ribbon-title">Integrations</div>
              {/*
              <div className="ribbon-sub">Create connections & mocks.</div>
              */}
            </div>

          </div>

          <div className="ribbon-content">
            <div className="group">
              <button className="pushable push-primary btn-sm" onClick={onOpenApiModal}>
                <span className="shadow"></span><span className="edge"></span>
                <span className="front">Create API</span>
              </button>
              <button className="pushable push-primary btn-sm" onClick={onOpenMockModal}>
                <span className="shadow"></span><span className="edge"></span>
                <span className="front">Create MOCK</span>
              </button>
            </div>
          </div>
        </section>

        {/* System & Monitoring */}
        <section className="ribbon">
          <div className="ribbon-header">
            <div>
              <div className="ribbon-title">System & Monitoring</div>
            </div>
          </div>

          <div className="ribbon-content">
            <div className="ribbon-actions">
              <button className="pushable push-primary btn-sm" onClick={onOpenLogs}>
                <span className="shadow"></span><span className="edge"></span>
                <span className="front">Logs ({logsCount})</span>
              </button>

              <button className="pushable push-primary btn-sm" onClick={onOpenAIConfig}>
                <span className="shadow"></span><span className="edge"></span><span className="front">AI Config</span>
              </button>

              <button className="pushable push-primary btn-sm" onClick={onOpenEnvironment}>
                  <span className="shadow"></span><span className="edge"></span>
                  <span className="front">Environment</span>
              </button>

            </div>
          </div>
        </section>

      </div>
    </div>
  );
}

/* ---------- helpers ---------- */
function KV({ label, value }: { label: string; value: string }) {
  return (
    <span className="ribbon-kv">
      <strong>{label}:</strong> {value}
    </span>
  );
}

function rowClass(selected: boolean) {
  return ["ribbon-row", selected ? "ribbon-row--selected" : ""].join(" ");
}
